import React, { useEffect, useState } from 'react';
import { audio } from '../game/audio';
import CoinStore from './CoinStore';
import Vault from './Vault';
import VaultCrack from './VaultCrack';
import AdSlot from '../components/AdSlot';
import {
  Play, Coins, KeyRound, Palette, ShoppingBag, Lock, Flame, Trophy,
  BookOpen, Settings, Volume2, VolumeX, Music, X, Gamepad2, Package, Award,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const HOW = [
  { icon: Gamepad2, t: 'Move & Explore', d: 'WASD or drag the joystick to roam the neon city. Aim with your mouse.' },
  { icon: Flame, t: 'Smash Machines', d: 'Punch (L-Click) or Kick (R-Click) vending machines. Break them for snacks — no paying!' },
  { icon: Package, t: 'Snatch Snacks', d: 'Snacks fly out with physics. Run over them to collect. Golden ⭐ = 100 pts.' },
  { icon: Lock, t: 'Dodge Guards', d: 'Guards see and hear you. Double-tap a direction or Space to dash — dashing gives brief immunity!' },
  { icon: Coins, t: 'Grab Power-ups', d: 'Speed, Silent Mode, Super Smash, Shield, Magnet, Double Score & Invisibility. Watch your stamina bar!' },
  { icon: Trophy, t: 'Top the Board', d: 'Highest score when the timer hits zero wins. Play safe or take the risk!' },
];

export default function MainMenu({ profile, settings, onPlay, onCustomize, onSettings, onChange }) {
  const [tab, setTab] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    fetch(API + '/leaderboard').then((r) => r.json()).then((d) => setBoard(d.leaderboard || [])).catch(() => {});
  }, [tab]);

  const play = () => { audio.resume(); onPlay(); };
  const owned = profile.owned || {};
  const unlocks = Object.values(owned).reduce((n, arr) => n + (arr ? arr.length : 0), 0);

  const NAV = [
    { key: 'store', label: 'Premium Store', Icon: ShoppingBag, cls: 'store', testid: 'menu-store-btn' },
    { key: 'customize', label: 'Customize', Icon: Palette, onClick: onCustomize, testid: 'menu-customize-btn' },
    { key: 'vault', label: 'Mystery Vault', Icon: KeyRound, cls: 'vault', testid: 'menu-vault-btn' },
    { key: 'crack', label: 'Crack the Vault', Icon: Flame, cls: 'crack', testid: 'menu-crack-btn' },
    { key: 'board', label: 'Leaderboard', Icon: Trophy, testid: 'menu-leaderboard-btn' },
    { key: 'how', label: 'How to Play', Icon: BookOpen, testid: 'menu-howto-btn' },
    { key: 'settings', label: 'Settings', Icon: Settings, testid: 'menu-settings-btn' },
  ];

  return (
    <div className="so-menu" data-testid="main-menu">
      <div className="so-menu-bg" />

      {/* top currency + identity bar */}
      <div className="so-topbar">
        <div className="so-player-chip" onClick={onCustomize} data-testid="menu-player-chip">
          <span className="so-avatar" style={{ background: profile.color }} />
          <span className="so-chip-name">{profile.name}</span>
          <small>edit</small>
        </div>
        <div className="so-topbar-right">
          <div className="so-keys-chip" onClick={() => setTab('vault')} data-testid="menu-keys-chip">
            <KeyRound size={15} /> {profile.keys || 0}
          </div>
          <div className="so-coins-chip" onClick={() => setTab('store')} data-testid="menu-coins-chip">
            <Coins size={15} /> {(profile.coins || 0).toLocaleString()} <b>+ Get</b>
          </div>
        </div>
      </div>

      <div className="so-menu-inner">
        <div className="so-logo">
          <h1>SNACK<span>OUT</span><em>.io</em></h1>
          <p className="so-tagline">SNACK · SMASH · ESCAPE</p>
        </div>

        {/* stats snapshot */}
        <div className="so-stats-snapshot" data-testid="menu-stats">
          <div className="so-stat"><Coins size={16} /><b>{(profile.coins || 0).toLocaleString()}</b><small>Coins</small></div>
          <div className="so-stat"><KeyRound size={16} /><b>{profile.keys || 0}</b><small>Keycards</small></div>
          <div className="so-stat"><Award size={16} /><b>{unlocks}</b><small>Unlocks</small></div>
        </div>

        <button className="so-btn-play" onClick={play} data-testid="menu-play-btn"><Play size={26} fill="currentColor" /> PLAY NOW</button>

        <div className="so-bento" data-testid="menu-bento">
          {NAV.map(({ key, label, Icon, cls, onClick, testid }) => (
            <button key={key} className={'so-bento-btn' + (cls ? ' ' + cls : '')}
              onClick={onClick || (() => setTab(key))} data-testid={testid}>
              <Icon size={22} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        <div className="so-menu-note">8–20 players · 4 min matches · real-time heist</div>

        <AdSlot label="Sponsored Heist Briefing" testid="adsense-menu-slot" style={{ marginTop: 18 }} />
      </div>

      {tab === 'store' && <CoinStore onClose={() => setTab(null)} />}
      {tab === 'vault' && <Vault profile={profile} onChange={onChange} onClose={() => setTab(null)} />}
      {tab === 'crack' && <VaultCrack profile={profile} onChange={onChange} onClose={() => setTab(null)} />}

      {tab && !['store', 'vault', 'crack'].includes(tab) && (
        <div className="so-overlay" onClick={() => setTab(null)}>
          <div className="so-panel" onClick={(e) => e.stopPropagation()} data-testid={'panel-' + tab}>
            <button className="so-panel-x" onClick={() => setTab(null)} data-testid="panel-close"><X size={18} /></button>

            {tab === 'how' && (
              <>
                <h2>How to Play</h2>
                <div className="so-how-grid">
                  {HOW.map((h) => (
                    <div key={h.t} className="so-how-card">
                      <span className="so-how-icon"><h.icon size={22} /></span>
                      <div><b>{h.t}</b><p>{h.d}</p></div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab === 'board' && (
              <>
                <h2><Trophy size={20} style={{ verticalAlign: '-3px' }} /> All-Time Leaderboard</h2>
                <div className="so-board" data-testid="alltime-leaderboard">
                  {board.length === 0 && <p className="so-empty">No scores yet — be the first!</p>}
                  {board.map((r, i) => (
                    <div key={i} className={'so-board-row' + (i < 3 ? ' top' : '')}>
                      <span className="so-board-rank">{['🥇', '🥈', '🥉'][i] || i + 1}</span>
                      <span className="so-board-name">{r.name}</span>
                      <span className="so-board-score">{r.score}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {tab === 'settings' && (
              <SettingsPanel settings={settings} onSettings={onSettings} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ settings, onSettings }) {
  const [s, setS] = useState(settings);
  const toggle = (k) => {
    const next = { ...s, [k]: !s[k] };
    setS(next); onSettings(next);
    if (k === 'sound') audio.setSfx(next.sound);
    if (k === 'music') audio.setMusic(next.music);
  };
  return (
    <>
      <h2>Settings</h2>
      <div className="so-settings">
        <label className="so-toggle" data-testid="setting-sound">
          <span>{s.sound ? <Volume2 size={18} /> : <VolumeX size={18} />} Sound Effects</span>
          <button className={s.sound ? 'on' : ''} onClick={() => toggle('sound')}>{s.sound ? 'ON' : 'OFF'}</button>
        </label>
        <label className="so-toggle" data-testid="setting-music">
          <span><Music size={18} /> Background Music</span>
          <button className={s.music ? 'on' : ''} onClick={() => toggle('music')}>{s.music ? 'ON' : 'OFF'}</button>
        </label>
      </div>
    </>
  );
}
